import React, { useState, useEffect, useMemo } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, FormFeedback, Row, Col, UncontrolledTooltip } from 'reactstrap'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { gql, useMutation, useQuery } from '@apollo/client'
import { LiabilityClaimSchema } from './IncidentModalsSchema'
import Select from 'react-select'

const CREATE_LIABILITY_CLAIM = gql`
  mutation CreateLiabilityClaim($input: CreateLiabilityClaimMutationInput!) {
    createLiabilityClaim(input: $input) {
      ok
      errors { 
            field 
            messages 
        }
    }
  }
`

const LIABILITY_STATUS_CHOICES = gql`
  query LiabilityStatusChoices {
    liabilityStatusChoices: __type(name: "IncidentsLiabilityClaimStatusChoices") {
      enumValues { 
            name 
            description 
        }
    }
  }
`
const ALL_TPAS = gql` query AllTpas($incidentId: ID) {
    allTpas(incidentId: $incidentId, claimType: "LIABILITY") { 
        edges { 
            node { 
                id 
                name
                tpaadjusterSet {
                    edges {
                        node {
                            id
                            name
                        }
                    }
                } 
            } 
        }
    }
  }
`

const CREATE_ADJUSTER = gql`
  mutation CreateAdjuster($input: CreateTPAAdjusterMutationInput!) {
    createTpaAdjuster(input: $input) {
      adjuster {
        id
        name
      }
      errors {
        field
        messages
      }
    }
  }
`

const AddLiabilityClaimModal = ({ isOpen, toggle, incidentId, onSuccess }) => {
    const [error, setError] = useState(null)
    const [adjusterOptions, setAdjusterOptions] = useState([])
    const [createClaim, { loading }] = useMutation(CREATE_LIABILITY_CLAIM)
    const { data: statusChoicesData } = useQuery(LIABILITY_STATUS_CHOICES)
    const { data: tpaData, refetch: refetchTpas } = useQuery(ALL_TPAS, { variables: { incidentId } })
    const [isAdjusterModalOpen, setAdjusterModalOpen] = useState(false)
    const [newAdjuster, setNewAdjuster] = useState({ name: '', phone: '', email: '', role: '' })
    const [createAdjuster, { loading: creatingAdjuster }] = useMutation(CREATE_ADJUSTER)
    const [adjusterError, setAdjusterError] = useState(null)

    const statusOptions = statusChoicesData?.liabilityStatusChoices?.enumValues?.map(opt => ({
        label: opt.description || opt.name,
        value: opt.name
    })) || []


    const tpaOptions = useMemo(() => {
        return tpaData?.allTpas?.edges?.map(edge => ({
            label: edge.node.name,
            value: edge.node.id,
            adjusters: edge.node.tpaadjusterSet.edges.map(adj => ({
                label: `${adj.node.name}`,
                value: adj.node.id
            }))
        })) || []
    }, [tpaData])

    const { control, handleSubmit, formState: { errors }, reset, watch } = useForm({
        resolver: yupResolver(LiabilityClaimSchema),
        defaultValues: { incidentId },
        mode: "onSubmit",
    })

    const selectedTpa = watch("tpaId")
    useEffect(() => {
        let tpaId = selectedTpa
        if (!selectedTpa && tpaOptions.length === 1) {
            tpaId = tpaOptions[0].value
        }
        const selectedTpaData = tpaOptions.find(tpa => tpa.value === tpaId)
        setAdjusterOptions(selectedTpaData?.adjusters || [])
        reset({ ...watch(), adjusterId: null, tpaId: tpaId })

    }, [selectedTpa, tpaOptions])

    const closeAndReset = () => {
        reset()
        toggle()
    }

    const onSubmit = async (values) => {
        const input = { ...values }
        try {
            const { data } = await createClaim({ variables: { input } })
            if (data?.createLiabilityClaim?.ok) {
                onSuccess && onSuccess()
                closeAndReset()
            }
            if (data?.createLiabilityClaim?.errors?.length) {
                setError(data.createLiabilityClaim.errors[0].messages[0])
            }
        } catch (e) {
            setError(e.message)
        }
    }

    const toggleAdjusterModal = () => setAdjusterModalOpen(!isAdjusterModalOpen)

    const handleCreateAdjuster = async () => {
        setAdjusterError(null)
        if (!newAdjuster.name) {
            setAdjusterError('Adjuster name is required.')
            return
        }
        const input = { ...newAdjuster, tpaId: selectedTpa }
        try {
            const { data } = await createAdjuster({ variables: { input } })
            if (data?.createTpaAdjuster?.adjuster) {
                const createdAdjuster = {
                    label: data.createTpaAdjuster.adjuster.name,
                    value: data.createTpaAdjuster.adjuster.id
                }
                await refetchTpas()
                setAdjusterOptions(prev => [...prev, createdAdjuster])
                reset({ ...watch(), adjusterId: createdAdjuster.value })
                toggleAdjusterModal()
                setNewAdjuster({ name: '', phone: '', email: '', role: '' })
            } else if (data?.createTpaAdjuster?.errors?.length) {
                setAdjusterError(data.createTpaAdjuster.errors[0].messages[0])
            }
        } catch (e) {
            setAdjusterError(e.message)
        }
    }

    return (
        <>
            <Modal isOpen={isOpen} toggle={closeAndReset} className='liability-claim-modal'>
                <ModalHeader>Add Liability Claim</ModalHeader>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        <Controller name="incidentId" control={control} render={({ field }) => <Input type="hidden" {...field} />} />
                        <Row>
                            <Col xs={6}>
                                <FormGroup>
                                    <Label for="externalId">External ID</Label>
                                    <Controller name="externalId" control={control} render={({ field }) => (
                                        <Input id="externalId" {...field} invalid={!!errors.externalId} placeholder="Enter external claim ID (if any)" />
                                    )} />
                                    <FormFeedback>{errors.externalId?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col xs={6}>
                                <FormGroup>
                                    <Label for="status">Status</Label>
                                    <Controller
                                        name="status"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                className="bos-custom-select"
                                                classNamePrefix="bos-select"
                                                inputId="status"
                                                options={statusOptions}
                                                value={statusOptions.find(opt => opt.value === field.value) || null}
                                                onChange={opt => field.onChange(opt ? opt.value : '')}
                                                isClearable
                                                placeholder="Select claim status"
                                            />
                                        )}
                                    />
                                    <FormFeedback style={{ display: errors.status ? 'block' : 'none' }}>{errors.status?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col xs={6}>
                                <FormGroup>
                                    <Label for="tpaId">TPA (Third Party Administrator)</Label>
                                    <Controller
                                        name="tpaId"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                className="bos-custom-select"
                                                classNamePrefix="bos-select"
                                                inputId="tpaId"
                                                isDisabled={tpaOptions.length === 1}
                                                options={tpaOptions}
                                                value={tpaOptions.find(opt => opt.value === field.value) || null}
                                                onChange={opt => {
                                                    field.onChange(opt ? opt.value : '')
                                                    refetchTpas() // Refetch TPAs when TPA selection changes
                                                }}
                                                isClearable
                                                placeholder="Select TPA"
                                            />
                                        )}
                                    />
                                    <FormFeedback style={{ display: errors.tpaId ? 'block' : 'none' }}>{errors.tpaId?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col xs={6}>
                                <Row>
                                    <Col xs={(!adjusterOptions.length && selectedTpa) ? 10 : 12}>
                                        <FormGroup>
                                            <Label for="adjusterId">TPA Adjuster</Label>
                                            <Controller
                                                name="adjusterId"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        className="bos-custom-select"
                                                        classNamePrefix="bos-select"
                                                        inputId="adjusterId"
                                                        isDisabled={!selectedTpa}
                                                        options={adjusterOptions}
                                                        value={adjusterOptions.find(opt => opt.value === field.value) || null}
                                                        onChange={opt => field.onChange(opt ? opt.value : '')}
                                                        isClearable
                                                        placeholder="Select TPA Adjuster"
                                                    />
                                                )}
                                            />
                                            <FormFeedback style={{ display: errors.adjusterId ? 'block' : 'none' }}>{errors.adjusterId?.message}</FormFeedback>
                                        </FormGroup>
                                    </Col>
                                    {!adjusterOptions.length && selectedTpa && (
                                        <Col xs={2} className='pl-0'>
                                            <Label>&nbsp;</Label>
                                            <Button color="primary" id="add-adjuster-button" onClick={toggleAdjusterModal}><i className='fa fa-plus' /> </Button>
                                            <UncontrolledTooltip target="add-adjuster-button" placement="top">
                                                Add Adjuster
                                            </UncontrolledTooltip>
                                        </Col>
                                    )}
                                </Row>
                            </Col>
                            <Col xs={6}>
                                <FormGroup>
                                    <Label for="filedDate">Filed Date</Label>
                                    <Controller name="filedDate" control={control} render={({ field }) => (
                                        <Input id="filedDate" type="date" {...field} invalid={!!errors.filedDate} placeholder="Select filed date" />
                                    )} />
                                    <FormFeedback>{errors.filedDate?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col xs={6}>
                                <FormGroup>
                                    <Label for="acknowledgmentDate">Acknowledgment Date</Label>
                                    <Controller name="acknowledgmentDate" control={control} render={({ field }) => (
                                        <Input id="acknowledgmentDate" type="date" {...field} invalid={!!errors.acknowledgmentDate} placeholder="Select acknowledgment date" />
                                    )} />
                                    <FormFeedback>{errors.acknowledgmentDate?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col xs={6}>
                                <FormGroup>
                                    <Label for="inLitigation">In Litigation</Label>
                                    <Controller
                                        name="inLitigation"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                className="bos-custom-select"
                                                classNamePrefix="bos-select"
                                                inputId="inLitigation"
                                                options={[
                                                    { value: true, label: 'True' },
                                                    { value: false, label: 'False' }
                                                ]}
                                                value={field.value === true ? { value: true, label: 'True' } : field.value === false ? { value: false, label: 'False' } : null}
                                                onChange={opt => field.onChange(opt ? opt.value : '')}
                                                isClearable
                                                placeholder="Claim in litigation?"
                                            />
                                        )}
                                    />
                                    <FormFeedback style={{ display: errors.inLitigation ? 'block' : 'none' }}>{errors.inLitigation?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col xs={6}>
                                <FormGroup>
                                    <Label for="reservedAmount">Reserved Amount</Label>
                                    <Controller name="reservedAmount" control={control} render={({ field }) => (
                                        <Input id="reservedAmount" type="number" {...field} invalid={!!errors.reservedAmount} placeholder="Reserved Amount" disabled />
                                    )} />
                                    <FormFeedback>{errors.reservedAmount?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col xs={6}>
                                <FormGroup>
                                    <Label for="paidAmount">Paid Amount</Label>
                                    <Controller name="paidAmount" control={control} render={({ field }) => (
                                        <Input id="paidAmount" type="number" {...field} invalid={!!errors.paidAmount} placeholder="Paid Amount" disabled />
                                    )} />
                                    <FormFeedback>{errors.paidAmount?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col xs={12}>
                                <FormGroup>
                                    <Label for="notes">Notes</Label>
                                    <Controller name="notes" control={control} render={({ field }) => (
                                        <Input id="notes" type="textarea" {...field} invalid={!!errors.notes} placeholder="Enter any additional notes or description" />
                                    )} />
                                    <FormFeedback>{errors.notes?.message}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                        {error && <div className="text-danger small mb-2">{error}</div>}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={closeAndReset}>Cancel</Button>
                        <Button color="primary" type="submit" disabled={loading}>Add</Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <Modal isOpen={isAdjusterModalOpen} toggle={toggleAdjusterModal}>
                <ModalHeader>Create Adjuster</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="name">Name*</Label>
                        <Input
                            id="name"
                            value={newAdjuster.name}
                            onChange={e => setNewAdjuster({ ...newAdjuster, name: e.target.value })}
                            placeholder="Enter adjuster name"
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="adjusterPhone">Phone</Label>
                        <Input
                            id="adjusterPhone"
                            value={newAdjuster.phone}
                            onChange={e => setNewAdjuster({ ...newAdjuster, phone: e.target.value })}
                            placeholder="Enter adjuster phone"
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="adjusterEmail">Email</Label>
                        <Input
                            id="adjusterEmail"
                            value={newAdjuster.email}
                            onChange={e => setNewAdjuster({ ...newAdjuster, email: e.target.value })}
                            placeholder="Enter adjuster email"
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="adjusterRole">Role</Label>
                        <Input
                            id="adjusterRole"
                            value={newAdjuster.role}
                            onChange={e => setNewAdjuster({ ...newAdjuster, role: e.target.value })}
                            placeholder="Enter adjuster role"
                        />
                    </FormGroup>
                    {adjusterError && <div className="text-danger small mb-2">{adjusterError}</div>}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleAdjusterModal} disabled={creatingAdjuster}>Cancel</Button>
                    <Button color="primary" onClick={handleCreateAdjuster} disabled={creatingAdjuster}>
                        {creatingAdjuster ? 'Creating...' : 'Create'}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}

export default AddLiabilityClaimModal